<template>
    <div class="dev-navigation">
        <ul class="file-list root">
            <li
                v-for="application in applicationTree"
                :key="application.identity"
                :data-testid="`application-${application.identity}`"
                :class="applicationClasses(application)"
                @click="toggleApplication(application.identity)"
            >
                <div class="entity-title" draggable="false">
                    <span
                        class="expand-icon"
                        :class="{ empty: application.entities.length === 0 }"
                    ></span>
                    <a
                        class="entity-name"
                        :class="{
                            selected: isApplicationSelected(
                                application.identity
                            ),
                        }"
                        :title="application.name"
                        href=""
                        @click.prevent.stop="
                            navigateToApplication(application.identity)
                        "
                    >
                        {{ application.name }}
                    </a>
                </div>
                <ul>
                    <li
                        v-for="entity in application.entities"
                        :key="entity.identity"
                        :data-testid="`application-entity-${entity.identity}`"
                        :class="entityClasses(entity.identity)"
                    >
                        <div class="entity-title" draggable="false">
                            <a
                                class="entity-name"
                                :class="{
                                    selected: isEntitySelected(entity.identity),
                                }"
                                :title="entity.name"
                                href=""
                                @click.prevent.stop="
                                    navigateToApplicationEntity(
                                        application.identity,
                                        entity.identity
                                    )
                                "
                            >
                                {{ entity.name }}
                            </a>
                        </div>
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useStekManager } from "@/composables/stek-manager";
import {
    sortApplicationsByName,
    useApplicationsManager,
    useApplicationsStore,
} from "@/stores/applications-store";

type NavigationApplicationEntity = {
    identity: string;
    name: string;
};

type NavigationApplication = {
    identity: string;
    name: string;
    entities: NavigationApplicationEntity[];
};

const route = useRoute();
const router = useRouter();
const { stek } = useStekManager();
const { applications, allApplicationsWithEntities } = useApplicationsManager();
const applicationsStore = useApplicationsStore();

const manuallyExpandedApplications = ref<Record<string, boolean>>({});

const routeApplicationId = computed(() => {
    return typeof route.params.applicationId === "string"
        ? route.params.applicationId
        : undefined;
});

const routeApplicationEntityId = computed(() => {
    return typeof route.params.applicationEntityId === "string"
        ? route.params.applicationEntityId
        : undefined;
});

const applicationTree = computed<NavigationApplication[]>(() => {
    if (!stek.value?.identity) return [];

    return sortApplicationsByName(
        allApplicationsWithEntities.value.map(({ application }) => application)
    ).map((application) => {
        const entry = allApplicationsWithEntities.value.find(
            ({ application: item }) => item.identity === application.identity
        );

        return {
            identity: application.identity,
            name: application.name || application.identity,
            entities: (entry?.entities ?? []).map((entity) => ({
                identity: entity.identity,
                name: entity.name || entity.identity,
            })),
        };
    });
});

const isApplicationSelected = (applicationId: string) => {
    return (
        route.name === "applicationPage" &&
        routeApplicationId.value === applicationId
    );
};

const isEntitySelected = (applicationEntityId: string) => {
    return (
        route.name === "applicationEntityPage" &&
        routeApplicationEntityId.value === applicationEntityId
    );
};

const isApplicationExpanded = (
    applicationId: string,
    entityCount: number
): boolean => {
    if (entityCount === 0) return false;
    if (applicationId in manuallyExpandedApplications.value) {
        return manuallyExpandedApplications.value[applicationId];
    }
    if (
        route.name === "applicationEntityPage" &&
        routeApplicationId.value === applicationId
    ) {
        return true;
    }

    return manuallyExpandedApplications.value[applicationId] ?? false;
};

const applicationClasses = (application: NavigationApplication) => ({
    "node-root": true,
    "application-root": true,
    selected: isApplicationSelected(application.identity),
    open: isApplicationExpanded(
        application.identity,
        application.entities.length
    ),
    closed: !isApplicationExpanded(
        application.identity,
        application.entities.length
    ),
});

const entityClasses = (applicationEntityId: string) => ({
    "node-root": true,
    "application-entity-root": true,
    selected: isEntitySelected(applicationEntityId),
    open: false,
    closed: true,
});

const toggleApplication = (applicationId: string) => {
    const application = applicationTree.value.find(
        (item) => item.identity === applicationId
    );

    if (!application || application.entities.length === 0) {
        return;
    }

    const expanded = isApplicationExpanded(
        applicationId,
        application.entities.length
    );
    manuallyExpandedApplications.value = {
        ...manuallyExpandedApplications.value,
        [applicationId]: !expanded,
    };
};

const baseRouteParams = computed(() => ({
    orgName: String(route.params.orgName),
    stekName: stek.value?.name || String(route.params.stekName),
}));

const navigateToApplication = (applicationId: string) => {
    router.push({
        name: "applicationPage",
        params: {
            ...baseRouteParams.value,
            applicationId,
        },
    });
};

const navigateToApplicationEntity = (
    applicationId: string,
    applicationEntityId: string
) => {
    router.push({
        name: "applicationEntityPage",
        params: {
            ...baseRouteParams.value,
            applicationId,
            applicationEntityId,
        },
    });
};

watch(
    [routeApplicationId, routeApplicationEntityId, () => route.name],
    () => {
        if (
            route.name === "applicationEntityPage" &&
            routeApplicationId.value
        ) {
            manuallyExpandedApplications.value = {
                ...manuallyExpandedApplications.value,
                [routeApplicationId.value]: true,
            };
        }
    },
    { immediate: true }
);

if (stek.value && !allApplicationsWithEntities.value.length) {
    applicationsStore.fetchAllApplicationsWithEntities();
} else if (stek.value && !applications.value.length) {
    applicationsStore.fetchApplications();
}
</script>

<style scoped lang="scss">
.dev-navigation {
    --ul-indent: 24px;
    --ul-icon-size: 24px;
    --expand-icon-size: 20px;

    height: 100%;
    overflow-y: auto;
}

.file-list.root {
    margin-left: 0;
    padding-top: 10px;
    padding-left: 0;
}

.file-list,
.file-list ul {
    list-style-type: none;
    font-size: 1em;
    color: var(--text-color);
    line-height: 1.5em;
    margin-left: -17px;
    padding-left: var(--ul-indent);
    border-left: 1px dotted #aaa;
}

.file-list li {
    position: relative;
}

li.node-root ul {
    transition: all 0.2s ease-in-out;
    padding-left: 0;
    margin-left: var(--ul-indent);
}

li.node-root.closed > ul {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
}

li.node-root.open > ul {
    opacity: 1;
    display: block;
}

li a {
    text-decoration: none;
    color: var(--text-color);
}

.file-list li .entity-title:hover {
    background: var(--stekz-primary-background);
}

.file-list li.selected > .entity-title {
    background: var(--surface-background);
}

.file-list li .entity-name {
    display: block;
    position: relative;
    padding: 8px;
    padding-left: calc(var(--ul-icon-size) + var(--ul-indent));
    margin-left: var(--expand-icon-size);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.file-list li .entity-name::before {
    display: block;
    position: absolute;
    top: 10px;
    left: calc(var(--ul-indent) / 2);
    width: var(--ul-icon-size);
    height: var(--ul-icon-size);
    mask-size: cover;
    background-color: var(--text-color);
    content: "";
}

.file-list li.selected > .entity-title > .entity-name,
.file-list li > .entity-title:hover > .entity-name {
    font-weight: bold;
}

.file-list li .expand-icon:not(.empty) {
    display: block;
    position: absolute;
    top: 10px;
    width: var(--expand-icon-size);
    height: var(--expand-icon-size);
    mask-size: cover;
    mask-repeat: no-repeat;
    background-color: var(--text-color);
    content: "";
}

.file-list li.node-root.closed > .entity-title > .expand-icon:not(.empty) {
    mask-image: url("/assets/primeicons/angle-right.svg");
}

.file-list li.node-root.open > .entity-title > .expand-icon:not(.empty) {
    mask-image: url("/assets/primeicons/angle-down.svg");
}

.file-list .application-root > .entity-title > .entity-name::before {
    mask-image: url("/assets/primeicons/database.svg");
}

.file-list .application-entity-root > .entity-title > .entity-name::before {
    mask-image: url("/assets/primeicons/list.svg");
}
</style>
